import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Clock, FileText, X, ChevronDown, BookOpen, Book, Bookmark, MessageSquareQuote, Sparkles, Gift } from "lucide-react";
import { MEDIA } from "@/content/media";
import { useContent } from "@/lib/ContentProvider";
import ArchGallery from "@/components/ArchGallery";

// Images choisies depuis les 5 sites existants — l'aperçu vers chaque univers
const ARCHES = [
  {
    key: "refuge",
    label: "Le refuge",
    cta: "Explorer le refuge",
    url: "/refuge",
    internal: true,
    image: MEDIA.refuge.bassin,
    imageAlt: "Bassin minéral du refuge",
    burnsClass: "cr-kenburns",
    symbol: (
      // Loupe — Explorer le refuge
      <svg viewBox="0 0 60 80" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="26" cy="34" r="10" />
        <line x1="34" y1="42" x2="42" y2="50" />
      </svg>
    ),
  },
  {
    key: "experiences",
    label: "Expériences",
    cta: "Découvrir les expériences",
    url: "/experiences",
    internal: true,
    image: MEDIA.experiences.bainDeFleurs,
    imageAlt: "Bain de fleurs au refuge",
    burnsClass: "cr-kenburns-alt",
    symbol: (
      // Signe + — Expériences
      <svg viewBox="0 0 60 80" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <line x1="30" y1="24" x2="30" y2="48" />
        <line x1="18" y1="36" x2="42" y2="36" />
      </svg>
    ),
  },
  {
    key: "avis",
    label: "Avis",
    cta: "Voir les avis",
    url: "/avis",
    internal: true,
    image: MEDIA.refuge.chambre,
    imageAlt: "Chambre cocon avec bougies",
    burnsClass: "cr-kenburns",
    symbol: (
      <svg viewBox="0 0 60 80" fill="none" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M16 30 C22 28, 26 32, 32 30 C36 28.5, 40 30, 44 30" />
        <path d="M18 42 C24 40, 28 44, 34 42 C38 40.5, 42 42, 44 42" opacity="0.7" />
        <path d="M20 54 C24 52.5, 28 55, 32 54 C36 53, 38 54, 40 54" opacity="0.45" />
      </svg>
    ),
  },
  {
    key: "tarifs",
    label: "Tarifs",
    cta: "Estimer mon séjour",
    url: "/tarifs",
    internal: true,
    image: MEDIA.refuge.cuisine,
    imageAlt: "Cuisine d'été du refuge",
    burnsClass: "cr-kenburns-alt",
    symbol: (
      <svg viewBox="0 0 60 80" fill="none" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M36 22 C28 22, 22 28, 22 36 C22 44, 28 50, 36 50 C32 46, 30 41, 30 36 C30 31, 32 26, 36 22 Z" />
      </svg>
    ),
  },
  {
    key: "bon-cadeau",
    label: "Bon cadeau",
    cta: "Offrir un moment",
    url: "/bon-cadeau",
    internal: true,
    image: MEDIA.experiences.decorChandelier,
    imageAlt: "Chandelier romantique — bon cadeau",
    burnsClass: "cr-kenburns",
    symbol: (
      <svg viewBox="0 0 60 80" fill="none" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {/* Ruban dessus */}
        <path d="M23 22 C21 20, 21 17, 23.5 15.5 C26 14, 29 16, 30 20 C31 16, 34 14, 36.5 15.5 C39 17, 39 20, 37 22" />
        {/* Corps du cadeau */}
        <rect x="18" y="26" width="24" height="6" rx="0.5" />
        <rect x="20" y="32" width="20" height="24" rx="0.5" />
        {/* Ruban vertical */}
        <line x1="30" y1="22" x2="30" y2="56" opacity="0.7" />
      </svg>
    ),
  },
  {
    key: "reserver",
    label: "Réserver",
    cta: "Voir les disponibilités",
    url: "/reserver",
    internal: true,
    image: MEDIA.refuge.porteArrivee,
    imageAlt: "Porte d'arrivée du refuge",
    burnsClass: "cr-kenburns",
    // La fenêtre Réserver n'affiche pas de galerie même si l'admin en ajoute.
    noGallery: true,
    symbol: (
      // Clé — Réserver
      <svg viewBox="0 0 60 80" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="22" cy="34" r="7" />
        <line x1="29" y1="34" x2="46" y2="34" />
        <line x1="40" y1="34" x2="40" y2="40" />
        <line x1="44" y1="34" x2="44" y2="38" />
      </svg>
    ),
  },
];

function Arch({ item, index, hovered, setHovered, size = "regular", mediaUrl, gallery }) {
  const isHovered = hovered === item.key;
  const isFeatured = size === "featured";
  // Liste d'images pour cette arche : override admin (gallery) ou l'image codée par défaut.
  // Certaines fenêtres (ex. Réserver) refusent la galerie même si l'admin fournit plusieurs images.
  let images;
  if (item.noGallery) {
    // On garde uniquement la première image (défaut ou 1er upload admin).
    images = [Array.isArray(gallery) && gallery.length > 0 ? gallery[0] : item.image];
  } else {
    images = Array.isArray(gallery) && gallery.length > 0 ? gallery : [item.image];
  }
  // outer rounding (arch)
  const outerRadius = "9999px 9999px 2px 2px";
  // inner rounding (inset frame)
  const innerRadius = "9999px 9999px 1px 1px";

  const commonProps = {
    "data-testid": `arch-${item.key}`,
    "aria-label": item.cta,
    onMouseEnter: () => setHovered(item.key),
    onMouseLeave: () => setHovered(null),
    className: "group block outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--cr-ruby)]/30 rounded-md cr-reveal",
    style: { animationDelay: `${0.08 * index + 0.05}s` },
  };

  const Wrapper = item.internal
    ? ({ children }) => <Link to={item.url} {...commonProps}>{children}</Link>
    : ({ children }) => <a href={item.url} target="_blank" rel="noopener noreferrer" {...commonProps}>{children}</a>;

  return (
    <Wrapper>
      <div
        className={`relative mx-auto w-full ${
          isFeatured
            ? "max-w-[125px] sm:max-w-[180px] md:max-w-[230px]"
            : "max-w-[80px] sm:max-w-[120px] md:max-w-[160px]"
        }`}
      >
        {/* ===== LA FENÊTRE ===== */}
        <div
          className="cr-window relative overflow-hidden transition-all duration-700 group-hover:-translate-y-1"
          style={{
            aspectRatio: isFeatured ? "1 / 1.55" : "1 / 1.7",
            borderRadius: outerRadius,
            border: "1px solid",
            borderColor: isHovered ? "rgba(139, 37, 48, 0.55)" : "rgba(139, 37, 48, 0.24)",
            background: "transparent",
          }}
        >
          {/* Image immersive en fond, avec ken-burns
              — mini-galerie automatique si plusieurs images (fondu enchaîné) */}
          <div
            className="cr-window-img absolute pointer-events-none overflow-hidden"
            style={{
              top: "5px",
              left: "5px",
              right: "5px",
              bottom: "5px",
              borderRadius: innerRadius,
            }}
          >
            <ArchGallery
              images={images}
              alt={item.imageAlt}
              burnsClass={item.burnsClass}
              mediaUrl={mediaUrl}
            />
            {/* voile très léger pour conserver la lisibilité éditoriale */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(180deg, rgba(255,253,249,0.05) 0%, rgba(255,253,249,0.0) 35%, rgba(46,31,24,0.18) 100%)",
              }}
            />
          </div>

          {/* Cadre intérieur — le châssis de fenêtre */}
          <div
            className="absolute pointer-events-none transition-all duration-700"
            style={{
              top: "5px",
              left: "5px",
              right: "5px",
              bottom: "5px",
              borderRadius: innerRadius,
              border: "1px solid",
              borderColor: isHovered ? "rgba(250, 244, 236, 0.55)" : "rgba(139, 37, 48, 0.18)",
            }}
          />

          {/* Symbole en léger filigrane, fixé au-dessus des images, en bas de la fenêtre.
              Reste stable pendant les transitions de galerie et ne masque pas la photo. */}
          <div
            className="cr-window-symbol absolute pointer-events-none flex items-end justify-center"
            style={{
              left: 0,
              right: 0,
              bottom: 0,
              top: "50%",
              color: "var(--cr-cream)",
              paddingBottom: isFeatured ? "12%" : "10%",
              filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.25))",
            }}
          >
            <div
              className={`${isFeatured ? "w-[26%]" : "w-[32%]"} aspect-[3/4] transition-all duration-1000 ease-out`}
              style={{
                opacity: isHovered ? 0.85 : 0.55,
                transform: isHovered ? "translateY(-2px) scale(1.05)" : "translateY(0) scale(1)",
              }}
            >
              {item.symbol}
            </div>
          </div>

          {/* halo doux extérieur au hover */}
          <div
            className="absolute -inset-3 pointer-events-none transition-opacity duration-700 -z-10"
            style={{
              opacity: isHovered ? 1 : 0,
              background: "radial-gradient(closest-side, rgba(232,165,176,0.22) 0%, transparent 70%)",
              borderRadius: "9999px 9999px 12px 12px",
            }}
          />
        </div>

        {/* Titre */}
        <p
          className={`mt-3 md:mt-5 text-center font-display font-light leading-tight tracking-tight transition-colors duration-700 ${
            isFeatured
              ? "text-base sm:text-xl md:text-[1.65rem]"
              : "text-[12px] sm:text-base md:text-[1.2rem]"
          }`}
          style={{ color: isHovered ? "var(--cr-ruby)" : "var(--cr-ink)" }}
        >
          {item.label}
        </p>

        {/* CTA — sous-titre élégant et lisible */}
        <p
          className={`mt-1.5 md:mt-2 text-center tracking-[0.04em] leading-tight transition-colors duration-700 flex items-center justify-center gap-1.5 w-full px-1 ${
            isFeatured ? "text-[11px] sm:text-[13px] md:text-[15px]" : "text-[9px] sm:text-[11px] md:text-[13px]"
          }`}
          style={{ color: isHovered ? "var(--cr-ruby)" : "var(--cr-ink-soft)" }}
        >
          <span className="font-display-italic">{item.cta}</span>
          <ArrowUpRight
            size={isFeatured ? 13 : 11}
            strokeWidth={1.4}
            className="transition-transform duration-500 flex-shrink-0"
            style={{
              transform: isHovered ? "translate(2px, -2px)" : "translate(0,0)",
              opacity: isHovered ? 1 : 0.6,
            }}
          />
        </p>

        {/* filet sous le CTA */}
        <div
          className="mx-auto mt-1 md:mt-2 h-[1px] transition-all duration-700"
          style={{
            width: isHovered ? (isFeatured ? "40px" : "26px") : "0px",
            background: "var(--cr-ruby)",
            opacity: 0.6,
          }}
        />
      </div>
    </Wrapper>
  );
}

export default function Home() {
  const [hovered, setHovered] = useState(null);
  const { mediaUrl, content } = useContent();
  // Galerie multi-images pilotée depuis l'admin (`homeArches`).
  // Format attendu : { reserver: ["path1", "path2"], refuge: [...], experiences: [...] }
  const homeArches = content?.homeArches || {};

  // Continuité visuelle depuis la porte : si le flag est présent, on démarre
  // avec un voile crème/doré (la lumière du seuil) qui se dissipe en fondu.
  const [fromDoor, setFromDoor] = useState(() => {
    try {
      const flag = sessionStorage.getItem("cr-entering-from-door") === "1";
      if (flag) sessionStorage.removeItem("cr-entering-from-door");
      return flag;
    } catch (_) {
      return false;
    }
  });
  useEffect(() => {
    if (!fromDoor) return;
    // On repeint avec l'overlay opaque, puis on lance le fondu 60 ms plus tard
    const t = setTimeout(() => setFromDoor(false), 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Modale "Conditions d'annulation"
  const [cancelOpen, setCancelOpen] = useState(false);
  useEffect(() => {
    if (!cancelOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setCancelOpen(false); };
    window.addEventListener("keydown", onKey);
    // Bloque le scroll de fond
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [cancelOpen]);

  // Arches (fenêtres) : Réserver · Le refuge · Expériences
  // Ronds (icônes) : Avis, Tarifs, Bon cadeau
  const arches = [
    ARCHES.find((a) => a.key === "reserver"),
    ARCHES.find((a) => a.key === "refuge"),
    ARCHES.find((a) => a.key === "experiences"),
  ].filter(Boolean);
  const rounds = (() => {
    // Guide access est configurable côté admin : visibilité, libellés, url, icône.
    const guideCfg = content?.menuGuideAccess || {};
    const GUIDE_ICONS = { book_open: BookOpen, book: Book, bookmark: Bookmark };
    const guideIcon = GUIDE_ICONS[guideCfg.icon_key] || BookOpen;
    const base = [
      { key: "avis",       label: "Avis",       cta: "Voir les avis",       url: "/avis",       Icon: MessageSquareQuote },
      { key: "tarifs",     label: "Tarifs",     cta: "Estimer mon séjour",  url: "/tarifs",     Icon: Sparkles },
      { key: "bon-cadeau", label: "Bon cadeau", cta: "Offrir un moment",    url: "/bon-cadeau", Icon: Gift },
    ];
    if (guideCfg.visible !== false) {
      base.push({
        key: "guide",
        label: (guideCfg.label || "Guide du voyageur").trim() || "Guide du voyageur",
        cta:   (guideCfg.cta   || "Ouvrir le guide").trim()   || "Ouvrir le guide",
        url:   (guideCfg.url   || "/guide").trim()             || "/guide",
        Icon: guideIcon,
      });
    }
    return base;
  })();

  return (
    <div
      data-testid="home-page"
      className="relative min-h-screen flex flex-col items-center"
      style={{ backgroundColor: "#FFFFFF" }}
    >
      {/* Voile de continuité — teinte crème/dorée émergeant du seuil */}
      <div
        aria-hidden="true"
        className="fixed inset-0 pointer-events-none z-[60]"
        style={{
          background: "#f5e7c8",
          opacity: fromDoor ? 1 : 0,
          transition: "opacity 1600ms ease-in-out",
        }}
      />

      {/* ============================================================
          HERO — Titre + 6 fenêtres/ronds + flèche
          ------------------------------------------------------------
          Occupe au minimum 100svh pour garantir la flèche visible
          au-dessus du fold sur tous les écrans (mobile compris).
          ============================================================ */}
      <section
        data-testid="home-hero"
        className="w-full flex flex-col items-center"
        style={{ minHeight: "100svh" }}
      >
        {/* ============================================================
            TITRE GÉNÉRAL — Bienvenue au refuge
            ============================================================ */}
        <header
          data-testid="home-title"
          className="w-full text-center pt-6 md:pt-12 pb-3 md:pb-6 px-5"
        >
          <p
            className="font-body text-[10px] md:text-[11px] tracking-[0.36em] uppercase mb-2 md:mb-3"
            style={{ color: "var(--cr-ruby)" }}
          >
            Curieux Refuge
          </p>
          <h1
            className="font-display font-light leading-[1.05] tracking-tight"
            style={{
              color: "var(--cr-ink)",
              fontSize: "clamp(1.75rem, 5.5vw, 3.4rem)",
            }}
          >
            Bienvenue au <span className="italic">refuge</span>
          </h1>
          <div className="flex items-center justify-center mt-3 md:mt-4">
            <span
              className="h-[1px] w-14"
              style={{ background: "var(--cr-gold)", opacity: 0.7 }}
            />
          </div>
        </header>

        {/* Groupe central (arches + ronds) — centré verticalement */}
        <div className="w-full px-3 sm:px-6 md:px-8 flex-1 flex flex-col justify-center">
          {/* Rangée 1 — 3 arches : Refuge · Expériences · Réserver */}
          <div className="grid grid-cols-3 gap-3 sm:gap-8 md:gap-12 w-full max-w-3xl mx-auto mb-6 md:mb-12">
            {arches.map((item, i) => (
              <Arch
                key={item.key}
                item={item}
                index={i}
                hovered={hovered}
                setHovered={setHovered}
                size="featured"
                mediaUrl={mediaUrl}
                gallery={homeArches[item.key]}
              />
            ))}
          </div>

          {/* Rangée 2 — 4 ronds : Avis · Tarifs · Bon cadeau · Guide du voyageur */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6 md:gap-10 w-full max-w-xl mx-auto">
            {rounds.map((item, i) => (
              <CircleLink
                key={item.key}
                item={item}
                index={i + 3}
                hovered={hovered}
                setHovered={setHovered}
              />
            ))}
          </div>
        </div>

        {/* "Découvrir la suite" retiré à la demande — aucun contenu supplémentaire sous les 7 accès */}
      </section>


      {/* GuideInvitation supprimé — l'accès Guide est désormais dans les 4 ronds */}
      {/* Section infos essentielles (Localisation · Horaires · Conditions) retirée
          visuellement. Les données restent en code et pourront être réutilisées
          plus tard sur une page dédiée.
          Note : la modale "Conditions d'annulation" (cancelOpen) reste montée mais
          n'est plus ouvrable depuis cette page (elle sera ré-attachée à la future
          page dédiée). */}

      {/* Modale — Conditions d'annulation */}
      {cancelOpen && (
        <div
          data-testid="cancel-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cancel-modal-title"
          className="fixed inset-0 z-[80] flex items-center justify-center px-4 py-8 md:p-8 cr-reveal"
          onClick={() => setCancelOpen(false)}
        >
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            aria-hidden="true"
          />
          <div
            className="relative w-full max-w-lg bg-refuge-bg rounded-2xl border border-refuge-border shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setCancelOpen(false)}
              data-testid="cancel-modal-close"
              aria-label="Fermer"
              className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-refuge-soft hover:text-refuge-ruby hover:bg-refuge-ruby/10 transition-all"
            >
              <X size={16} strokeWidth={1.4} />
            </button>
            <div className="p-6 md:p-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(232, 184, 189, 0.20)" }}>
                  <FileText className="w-4 h-4 text-refuge-ruby" strokeWidth={1.3} />
                </span>
                <div>
                  <p className="font-body text-[10px] tracking-[0.24em] uppercase text-refuge-ruby">Conditions</p>
                  <h3
                    id="cancel-modal-title"
                    className="font-heading text-xl md:text-2xl font-light text-refuge-text leading-tight"
                  >
                    Conditions <span className="italic">d'annulation</span>
                  </h3>
                </div>
              </div>
              <div className="space-y-3.5 font-body text-[14px] text-refuge-text/85 leading-relaxed">
                <p>
                  Un <span className="font-medium text-refuge-text">acompte de 50 €</span> est demandé à la réservation pour garantir vos dates. Il est <span className="italic">non remboursable</span> car il réserve exclusivement les dates choisies dès la confirmation.
                </p>
                <p>
                  Le <span className="font-medium text-refuge-text">solde restant</span> est automatiquement prélevé <span className="font-medium text-refuge-text">7 jours avant l'arrivée</span>.
                </p>
                <p>
                  Si vous devez annuler, faites-le <span className="font-medium text-refuge-text">avant cette échéance</span> pour éviter le prélèvement automatique du solde.
                </p>
                <p>
                  Une fois le solde prélevé (à partir de 7 jours avant l'arrivée), il devient également non remboursable.
                </p>
                <p className="italic text-refuge-soft pt-2 border-t border-refuge-border/60">
                  Une caution de 250 € est également préautorisée (aucun débit — voir la page <Link to="/reserver" onClick={() => setCancelOpen(false)} className="text-refuge-ruby underline hover:no-underline">Réserver</Link> pour le détail).
                </p>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={() => setCancelOpen(false)}
                  data-testid="cancel-modal-ok"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-refuge-ruby text-white font-body text-[11px] tracking-[0.18em] uppercase hover:bg-refuge-ruby/90 transition-all"
                >
                  J'ai compris
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * GuideInvitation — invitation immersive à ouvrir le Guide du Voyageur.
 * Un carnet raffiné entrouvert (SVG, tracé fin) qui respire doucement,
 * s'entrouvre légèrement au survol, et invite à cliquer sur "Ouvrir le Guide".
 */
function GuideInvitation() {
  return (
    <section
      data-testid="home-guide-invitation"
      aria-label="Le Guide du Voyageur"
      className="w-full max-w-4xl mx-auto px-5 sm:px-6 md:px-8 mt-10 md:mt-12"
    >
      <Link
        to="/guide"
        data-testid="home-guide-open-cta"
        className="cr-reveal cr-book-group group block relative outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--cr-ruby)]/30 rounded-3xl"
        style={{ animationDelay: "0.3s" }}
      >
        <div
          className="relative flex flex-col md:flex-row items-center gap-8 md:gap-12 px-6 sm:px-10 md:px-14 py-10 md:py-14 overflow-hidden"
          style={{
            borderRadius: "28px",
            background:
              "linear-gradient(180deg, rgba(255,253,249,0.95) 0%, rgba(250,244,236,0.88) 100%)",
            border: "1px solid rgba(139, 37, 48, 0.16)",
            boxShadow: "0 1px 0 rgba(255,255,255,0.6) inset",
          }}
        >
          {/* Halo doux au hover */}
          <div
            className="cr-book-halo absolute -inset-6 pointer-events-none"
            aria-hidden="true"
            style={{
              opacity: 0,
              background:
                "radial-gradient(closest-side, rgba(232,165,176,0.28) 0%, transparent 70%)",
            }}
          />

          {/* ===== Carnet ouvert ===== */}
          <div
            className="cr-book-breathe relative shrink-0"
            style={{ width: "180px", maxWidth: "38vw" }}
          >
            <BookSvg />
          </div>

          {/* ===== Texte + CTA ===== */}
          <div className="flex-1 text-center md:text-left">
            <p
              className="font-body text-[10px] tracking-[0.28em] uppercase mb-3"
              style={{ color: "var(--cr-ruby)" }}
            >
              Votre compagnon de séjour
            </p>
            <h2
              className="font-display font-light leading-[1.05] tracking-tight"
              style={{
                color: "var(--cr-ink)",
                fontSize: "clamp(1.8rem, 3.4vw, 2.6rem)",
              }}
            >
              Le Guide du <span className="italic">Voyageur</span>
            </h2>
            <p
              className="font-display italic font-light mt-3 mb-6 md:mb-7 max-w-md mx-auto md:mx-0"
              style={{
                color: "var(--cr-ink-soft)",
                fontSize: "clamp(0.95rem, 1.2vw, 1.05rem)",
                lineHeight: 1.55,
              }}
            >
              Un bel ouvrage à ouvrir avant, pendant et après votre séjour —
              tout ce qu'il faut savoir pour se sentir chez soi au Refuge.
            </p>

            <span
              className="cr-cta-pulse inline-flex items-center gap-3 px-6 md:px-7 py-3 md:py-3.5 rounded-full font-body text-[11px] md:text-[12px] tracking-[0.22em] uppercase transition-all duration-500 group-hover:translate-x-1"
              style={{
                background: "var(--cr-ruby)",
                color: "#FAF4EC",
                border: "1px solid var(--cr-ruby)",
              }}
            >
              <BookOpen size={15} strokeWidth={1.4} />
              Ouvrir le Guide
              <ArrowUpRight
                size={13}
                strokeWidth={1.6}
                className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </span>
          </div>
        </div>
      </Link>
    </section>
  );
}

/**
 * BookSvg — carnet raffiné entrouvert, tracé fin.
 * Couverture rubis à gauche qui bascule légèrement au hover,
 * pages crème visibles à droite, ruban doré fin.
 */
function BookSvg() {
  return (
    <svg
      viewBox="0 0 240 200"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-auto"
      aria-hidden="true"
    >
      {/* Ombre douce au sol */}
      <ellipse cx="120" cy="184" rx="86" ry="6" fill="rgba(46,31,24,0.10)" />

      {/* Pages crème (côté droit) */}
      <g className="cr-book-page-top">
        <path
          d="M120 46 C 150 40, 190 42, 214 50 L214 168 C 190 160, 150 160, 120 168 Z"
          fill="#FAF4EC"
          stroke="rgba(139, 37, 48, 0.22)"
          strokeWidth="1"
        />
        {/* Lignes de texte suggérées */}
        <line x1="140" y1="72"  x2="200" y2="66"  stroke="rgba(139, 37, 48, 0.35)" strokeWidth="0.8" strokeLinecap="round" />
        <line x1="140" y1="82"  x2="196" y2="76"  stroke="rgba(139, 37, 48, 0.22)" strokeWidth="0.6" strokeLinecap="round" />
        <line x1="140" y1="92"  x2="204" y2="86"  stroke="rgba(139, 37, 48, 0.22)" strokeWidth="0.6" strokeLinecap="round" />
        <line x1="140" y1="102" x2="192" y2="96"  stroke="rgba(139, 37, 48, 0.22)" strokeWidth="0.6" strokeLinecap="round" />
        <line x1="140" y1="118" x2="200" y2="112" stroke="rgba(139, 37, 48, 0.22)" strokeWidth="0.6" strokeLinecap="round" />
        <line x1="140" y1="128" x2="196" y2="122" stroke="rgba(139, 37, 48, 0.22)" strokeWidth="0.6" strokeLinecap="round" />
        <line x1="140" y1="138" x2="188" y2="132" stroke="rgba(139, 37, 48, 0.22)" strokeWidth="0.6" strokeLinecap="round" />
      </g>

      {/* Pages arrière (empilement subtil) */}
      <path
        d="M120 46 C 92 40, 52 42, 28 50 L28 168 C 52 160, 92 160, 120 168 Z"
        fill="#F2E8D9"
        stroke="rgba(139, 37, 48, 0.14)"
        strokeWidth="0.8"
      />

      {/* Couverture gauche (rubis) — pivote au hover */}
      <g className="cr-book-cover-left">
        <path
          d="M120 44 C 92 36, 52 38, 26 46 L26 166 C 52 158, 92 158, 120 166 Z"
          fill="#8B2530"
          stroke="#6E1A24"
          strokeWidth="1"
        />
        {/* Filet doré vertical */}
        <line
          x1="38" y1="58" x2="38" y2="154"
          stroke="#B89968" strokeWidth="0.8" opacity="0.85"
        />
        {/* Cartouche titre */}
        <rect
          x="52" y="76" width="52" height="30"
          rx="1"
          fill="none"
          stroke="#B89968"
          strokeWidth="0.6"
          opacity="0.75"
        />
        {/* Deux traits calligraphiques suggérant "Guide" */}
        <line x1="60" y1="88"  x2="96" y2="86"  stroke="#B89968" strokeWidth="0.9" strokeLinecap="round" />
        <line x1="60" y1="95"  x2="88" y2="94"  stroke="#B89968" strokeWidth="0.7" strokeLinecap="round" opacity="0.75" />
        {/* Petite étoile / lune stylisée */}
        <circle cx="78" cy="122" r="3" fill="none" stroke="#B89968" strokeWidth="0.6" />
        <line x1="78" y1="128" x2="78" y2="134" stroke="#B89968" strokeWidth="0.5" opacity="0.6" />
      </g>

      {/* Reliure centrale */}
      <line x1="120" y1="44" x2="120" y2="168" stroke="rgba(46,31,24,0.35)" strokeWidth="0.8" />

      {/* Ruban marque-page (or) */}
      <path
        d="M148 46 L148 88 L154 84 L160 88 L160 46 Z"
        fill="#B89968"
        opacity="0.9"
      />
    </svg>
  );
}

/**
 * CircleLink — bouton rond élégant (icône lucide dans un cercle
 * bordé rubis, label en dessous). Utilisé pour Avis · Tarifs · Bon
 * cadeau — les entrées secondaires du menu d'accueil.
 */
function CircleLink({ item, index, hovered, setHovered }) {
  const { Icon, label, cta, url, key } = item;
  const isHovered = hovered === key;
  return (
    <Link
      to={url}
      data-testid={`circle-${key}`}
      aria-label={cta}
      onMouseEnter={() => setHovered(key)}
      onMouseLeave={() => setHovered(null)}
      className="cr-reveal group flex flex-col items-center gap-2 outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--cr-ruby)]/30 rounded-full"
      style={{ animationDelay: `${0.08 * index + 0.05}s` }}
    >
      <span
        className="flex items-center justify-center rounded-full transition-all duration-700 group-hover:-translate-y-1"
        style={{
          width: "clamp(58px, 14vw, 82px)",
          height: "clamp(58px, 14vw, 82px)",
          background: isHovered ? "rgba(232, 184, 189, 0.22)" : "rgba(232, 184, 189, 0.10)",
          border: `1px solid ${isHovered ? "rgba(139, 37, 48, 0.55)" : "rgba(139, 37, 48, 0.30)"}`,
          boxShadow: isHovered ? "0 8px 20px rgba(139, 37, 48, 0.12)" : "0 2px 6px rgba(139, 37, 48, 0.06)",
        }}
      >
        <Icon
          size={22}
          strokeWidth={1.3}
          style={{
            color: "var(--cr-ruby)",
            transition: "transform 0.5s",
            transform: isHovered ? "scale(1.08)" : "scale(1)",
          }}
        />
      </span>
      <p
        className="text-center font-display font-light leading-tight transition-colors duration-500"
        style={{
          color: isHovered ? "var(--cr-ruby)" : "var(--cr-ink)",
          fontSize: "clamp(0.85rem, 1.6vw, 1rem)",
        }}
      >
        {label}
      </p>
      <span
        className="font-display italic tracking-[0.03em] transition-opacity duration-500"
        style={{
          color: "var(--cr-ink-soft)",
          fontSize: "clamp(0.7rem, 1.1vw, 0.78rem)",
          opacity: isHovered ? 1 : 0.6,
        }}
      >
        {cta}
      </span>
    </Link>
  );
}

function InfoCard({ testid, index, Icon, eyebrow, title, sub, action }) {
  return (
    <div
      data-testid={testid}
      className="cr-reveal group relative flex flex-col items-center text-center px-6 py-8 sm:py-9 transition-all duration-700"
      style={{
        animationDelay: `${0.5 + 0.1 * index}s`,
        borderRadius: "9999px 9999px 6px 6px",
        border: "1px solid rgba(139, 37, 48, 0.18)",
        background: "linear-gradient(180deg, rgba(255,253,249,0.6) 0%, rgba(255,253,249,0) 100%)",
      }}
    >
      {/* Petite arche décorative avec l'icône */}
      <div
        className="flex items-center justify-center w-12 h-14 mb-4 transition-transform duration-700 group-hover:-translate-y-0.5"
        style={{
          borderRadius: "9999px 9999px 2px 2px",
          border: "1px solid rgba(139, 37, 48, 0.28)",
          background: "rgba(232, 184, 189, 0.10)",
        }}
      >
        <Icon
          className="w-4 h-4 md:w-[18px] md:h-[18px] transition-colors duration-500"
          strokeWidth={1.3}
          style={{ color: "var(--cr-ruby)" }}
        />
      </div>

      <p
        className="font-body text-[10px] tracking-[0.24em] uppercase mb-2"
        style={{ color: "var(--cr-ruby)" }}
      >
        {eyebrow}
      </p>
      <p
        className="font-display font-light text-lg md:text-xl leading-snug"
        style={{ color: "var(--cr-ink)" }}
      >
        {title}
      </p>
      {sub && (
        <p
          className="font-display italic font-light text-sm md:text-base mt-1"
          style={{ color: "var(--cr-ink-soft)" }}
        >
          {sub}
        </p>
      )}

      {action && (action.onClick ? (
        <button
          type="button"
          onClick={action.onClick}
          data-testid={`${testid}-cta`}
          className="mt-5 inline-flex items-center gap-1.5 px-5 py-2 rounded-full border font-body text-[11px] tracking-[0.16em] uppercase transition-all duration-500 cursor-pointer"
          style={{
            borderColor: "rgba(139, 37, 48, 0.35)",
            color: "var(--cr-ruby)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "var(--cr-ruby)";
            e.currentTarget.style.color = "#fff";
            e.currentTarget.style.borderColor = "var(--cr-ruby)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = "var(--cr-ruby)";
            e.currentTarget.style.borderColor = "rgba(139, 37, 48, 0.35)";
          }}
        >
          {action.label}
          <ArrowUpRight size={12} strokeWidth={1.5} />
        </button>
      ) : (
        <a
          href={action.href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${testid}-cta`}
          className="mt-5 inline-flex items-center gap-1.5 px-5 py-2 rounded-full border font-body text-[11px] tracking-[0.16em] uppercase transition-all duration-500"
          style={{
            borderColor: "rgba(139, 37, 48, 0.35)",
            color: "var(--cr-ruby)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "var(--cr-ruby)";
            e.currentTarget.style.color = "#fff";
            e.currentTarget.style.borderColor = "var(--cr-ruby)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = "var(--cr-ruby)";
            e.currentTarget.style.borderColor = "rgba(139, 37, 48, 0.35)";
          }}
        >
          {action.label}
          <ArrowUpRight size={12} strokeWidth={1.5} />
        </a>
      ))}

      {/* Filet doux qui se révèle au hover */}
      <div
        className="absolute bottom-3 h-[1px] transition-all duration-700"
        style={{
          width: "0px",
          background: "var(--cr-ruby)",
          opacity: 0.5,
        }}
      />
    </div>
  );
}
